import { DialogCloseEvent } from './dialog.js';
import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { Component } from '@theme/component';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;
// Click zoom. Each click on the photo zooms in ×1.5 toward the click point;
// once the ceiling is reached the next click zooms back out to fit.
//   • Phones (<750px): the eMag-style THREE-step ladder up to 3.375× is kept
//     (client 2026-07), alongside pinch / double-tap.
//   • Tablet + desktop (>=750px): a SINGLE step — click zooms in once, the next
//     click zooms back to fit, and the mouse wheel no longer zooms. Removes the
//     "zoom in multiple times in fullscreen" behaviour Jan flagged (2026-07-24).
const CLICK_ZOOM_FACTOR = 1.5;
const CLICK_ZOOM_MAX_MOBILE = CLICK_ZOOM_FACTOR ** 3; // 3.375 (phone ladder)

/**
 * @typedef {object} Refs
 * @property {HTMLImageElement} image - The image element to zoom and drag.
 */

/** @extends {Component<Refs>} */
export class DragZoomWrapper extends Component {
  requiredRefs = ['image'];
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {{width: number, height: number, centerX: number, centerY: number} | null} */
  #metrics = null;

  /** One-shot: set on mouseup after a genuine drag, consumed by the next click. */
  #dragJustEnded = false;

  get #image() {
    return this.refs.image;
  }

  /**
   * Open FITTED on every breakpoint (contain), so the whole image shrinks to fit
   * the container rather than opening magnified — the client wants the modal
   * container full-bleed with the image contained on small screens (2026-07-24).
   * Pinch / double-tap / click still zoom in from there.
   */
  #initialScale() {
    return MIN_ZOOM;
  }

  /**
   * Cached wrapper geometry. `#constrainTranslation` used to read clientWidth,
   * clientHeight AND getBoundingClientRect() on every animation frame, forcing
   * three synchronous layouts per frame mid-gesture — the source of the jerky
   * panning. The wrapper can't move during a gesture, so measure once and
   * invalidate on resize / reset.
   */
  #measure() {
    return this.#metrics ?? this.#remeasure();
  }

  /**
   * Force-refresh the cache. One-shot operations (click hit-test, gesture
   * start) always use this: cache invalidation rides on ResizeObserver, and RO
   * delivery is tied to rendering steps — in a hidden/occluded tab, or in the
   * frame right after the dialog opens, it may not have fired yet, leaving
   * stale zeros that would misread a click on the photo as "outside" and close
   * the modal. A single layout read per discrete action is cheap; only the
   * per-frame pan path relies on the cache.
   */
  #remeasure() {
    const rect = this.getBoundingClientRect();
    this.#metrics = {
      width: rect.width,
      height: rect.height,
      centerX: rect.left + rect.width / 2,
      centerY: rect.top + rect.height / 2,
    };
    return this.#metrics;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.#image) return;

    this.#initResizeListener();
    window.addEventListener(DialogCloseEvent.eventName, this.#resetZoom);

    // Previously bailed out here on desktop, so the zoom modal bound no
    // listeners at all and a mouse could never magnify anything.
    this.#scale = this.#initialScale();
    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    // Mouse equivalents: click to step the zoom ladder (via the dialog's
    // handleImageClick call), wheel for fine zoom, drag to pan.
    this.addEventListener('wheel', this.#handleWheel, options);
    this.addEventListener('mousedown', this.#handleMouseDown, { signal });

    // Initialize transform immediately
    this.#updateTransform();
  }

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    // Tablet + desktop (>=750px) no longer wheel-zoom — the single click toggle
    // is the only zoom there (Jan, 2026-07-24). Phones keep wheel zoom (a no-op
    // on touch anyway; harmless if a phone ever has a wheel device attached).
    if (!isMobileBreakpoint()) return;
    preventDefault(event);
    // ~1.0015 per deltaY unit gives a smooth ramp on both trackpads and wheels.
    this.#zoomAround(event.clientX, event.clientY, this.#scale * Math.pow(1.0015, -event.deltaY));
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    // Only pan when there is overflow to pan into.
    if (this.#scale <= MIN_ZOOM || event.button !== 0) return;
    preventDefault(event);

    this.#remeasure();
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };

    const { signal } = this.#controller;
    window.addEventListener('mousemove', this.#handleMouseMove, { signal });
    window.addEventListener('mouseup', this.#handleMouseUp, { once: true, signal });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;

    const dx = event.clientX - this.#startPosition.x;
    const dy = event.clientY - this.#startPosition.y;

    if (!this.#hasDraggedBeyondThreshold && getDistance({ x: 0, y: 0 }, { x: dx, y: dy }) > DRAG_THRESHOLD) {
      this.#hasDraggedBeyondThreshold = true;
    }

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;
    this.#requestUpdateTransform();
  };

  #handleMouseUp = () => {
    this.#isDragging = false;
    window.removeEventListener('mousemove', this.#handleMouseMove);
    // The click that ends a real pan must not be treated as a zoom request.
    this.#dragJustEnded = this.#hasDraggedBeyondThreshold;
  };

  /**
   * Viewport rect of the visible image CONTENT (not the wrapper, which spans
   * the whole dialog): object-fit: contain base rect, then the current
   * scale/translate. `transform: scale(s) translate(t)` applies translate
   * first, so the visual offset is s·t — matching the drag maths, which stores
   * translate pre-divided by scale.
   */
  #imageContentRect() {
    const img = this.#image;
    const m = this.#remeasure();
    if (!img || !m.width || !m.height) return null;

    let nw = img.naturalWidth;
    let nh = img.naturalHeight;
    if (!(nw > 0 && nh > 0)) {
      nw = m.width;
      nh = m.width;
    }

    const imageRatio = nw / nh;
    const wrapperRatio = m.width / m.height;
    let w, h;
    if (imageRatio > wrapperRatio) {
      w = m.width;
      h = w / imageRatio;
    } else {
      h = m.height;
      w = h * imageRatio;
    }

    const s = this.#scale;
    const cx = m.centerX + s * this.#translate.x;
    const cy = m.centerY + s * this.#translate.y;
    return { left: cx - (w * s) / 2, right: cx + (w * s) / 2, top: cy - (h * s) / 2, bottom: cy + (h * s) / 2 };
  }

  /**
   * Whether a viewport point lands on the photo itself (vs the letterbox).
   * @param {number} clientX
   * @param {number} clientY
   */
  hitsImage(clientX, clientY) {
    const rect = this.#imageContentRect();
    if (!rect) return false;
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }

  /**
   * The click-zoom ceiling for the current breakpoint: the 3-step ladder on
   * phones, a single ×1.5 step on tablet + desktop.
   */
  #clickZoomMax() {
    return isMobileBreakpoint() ? CLICK_ZOOM_MAX_MOBILE : CLICK_ZOOM_FACTOR;
  }

  /**
   * True when the click ladder is exhausted — the next click on the photo
   * zooms back out to fit. The cursor follower reads this to flip the
   * magnifier glyph from plus to minus.
   */
  get nextClickZoomsOut() {
    return this.#scale >= this.#clickZoomMax() - 0.01;
  }

  /**
   * A click bubbled up to the slide. Decides what it means:
   *  - 'zoomed'  → hit the photo: stepped in ×1.5 toward the click point, or —
   *                after the third step — back out to fit (native-viewer ladder)
   *  - 'ignored' → the click merely ended a pan — do nothing
   *  - 'outside' → letterbox / white area — the dialog should close
   *
   * Decided here rather than via stopPropagation because Horizon binds
   * `on:click` as a CAPTURE listener on `document` (component.js) — it always
   * fires before any listener on this element and cannot be cancelled from it.
   * @param {number} clientX
   * @param {number} clientY
   * @returns {'zoomed' | 'ignored' | 'outside'}
   */
  handleImageClick(clientX, clientY) {
    if (this.#dragJustEnded) {
      this.#dragJustEnded = false;
      return 'ignored';
    }
    if (!this.hitsImage(clientX, clientY)) return 'outside';

    if (this.nextClickZoomsOut) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
      this.#cancelAnimationFrame();
      this.#updateTransform();
    } else {
      this.#zoomAround(clientX, clientY, Math.min(this.#scale * CLICK_ZOOM_FACTOR, this.#clickZoomMax()));
    }
    return 'zoomed';
  }

  /**
   * Scale toward a viewport point, keeping that point visually anchored.
   * @param {number} clientX
   * @param {number} clientY
   * @param {number} nextScale
   */
  #zoomAround(clientX, clientY, nextScale) {
    const oldScale = this.#scale;
    const scale = clamp(nextScale, MIN_ZOOM, MAX_ZOOM);
    if (scale === oldScale) return;

    const { centerX, centerY } = this.#measure();
    this.#scale = scale;

    if (scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
    } else {
      const scaleDelta = scale / oldScale - 1.0;
      this.#translate.x -= ((clientX - centerX) * scaleDelta) / scale;
      this.#translate.y -= ((clientY - centerY) * scaleDelta) / scale;
      this.#hasManualZoom = true;
    }

    // Discrete zoom steps paint immediately — deferring a one-shot change to a
    // frame adds latency and leaves `#scale` and the CSS var out of sync if the
    // frame is ever dropped. Continuous panning still batches via rAF.
    this.#cancelAnimationFrame();
    this.#updateTransform();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(DialogCloseEvent.eventName, this.#resetZoom);
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    this.#metrics = null;

    if (!this.#initialized) {
      this.#initEventListeners();
    }

    if (this.#initialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      // Early exit if touches are invalid
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      // Avoid object allocation by passing touches directly
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      // Use performance.now() for better precision and performance
      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      // Early exit if too much time has passed
      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      // Only check distance if we have a previous tap within time window
      if (this.#lastTapPosition) {
        // Distance calculation with early exit
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          // This is a double-tap, handle zoom toggle
          this.#handleDoubleTapFromTouch(touch);
          this.#lastTapTime = 0; // Reset to prevent triple-tap
          this.#lastTapPosition = null;
          return;
        }
      }

      // Store tap info for potential double-tap detection
      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * Start a zoom gesture with two touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    // Calculate initial distance between touches
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * Start a drag gesture with a single touch
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * Store tap information for double-tap detection
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Handle double-tap zoom toggle from touch
   * @param {Touch} touch - The touch where the double-tap occurred
   */
  #handleDoubleTapFromTouch(touch) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;

    // If manual zoom has been used, reset to 1x
    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM; // 1x
      this.#hasManualZoom = false; // Reset the flag
      this.#translate = { x: 0, y: 0 }; // Center the image
    } else {
      // Toggle between zoom levels: 1x ↔ 1.5x
      const tolerance = 0.05; // Small tolerance for floating point comparison

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        // Currently at 1x, go to 1.5x
        targetZoom = DEFAULT_ZOOM;
      } else {
        // Currently at 1.5x or any other level, go to 1x
        targetZoom = MIN_ZOOM;
      }
    }

    // If we're not going to 1x, adjust translation to center zoom on the tap point
    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);

      // Calculate the distance from tap point to container center
      const distanceFromCenter = {
        x: touch.clientX - containerCenter.x,
        y: touch.clientY - containerCenter.y,
      };

      // Adjust translation to center zoom on the tap point
      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      // Going to 1x, set the scale and center the image
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 }; // Center the image when going to 1x
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    preventDefault(event);

    const touchCount = event.touches.length;

    if (touchCount === 2) {
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  /**
   * Process zoom gesture from touches
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    // Calculate midpoint directly without object allocation
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    // Calculate current distance between touches
    const currentDistance = getDistance(touch1, touch2);

    const oldScale = this.#scale;

    // Calculate and apply new scale
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    // Mark that manual zoom has been used
    this.#hasManualZoom = true;

    // Adjust translation to keep the pinch midpoint stationary
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;

    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;

    // Calculate how the image position needs to change to keep the midpoint stationary
    const scaleDelta = this.#scale / oldScale - 1.0;

    // Apply correction to prevent zooming on the opposite side of the midpoint
    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * Process drag gesture from touch
   * @param {Touch} touch
   */
  #processDragGesture(touch) {
    // Check if we've moved beyond the drag threshold
    const distance = getDistance(touch, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      // Movement is too small, don't process as drag yet
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    // Calculate movement deltas for translation
    const dx = touch.clientX - this.#startPosition.x;
    const dy = touch.clientY - this.#startPosition.y;

    // Calculate new translation directly
    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();

      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Constrain image translation to keep it within the viewport
   */
  #constrainTranslation() {
    if (!this.#image) return;

    // Cached — see #measure(). Reading layout here per frame used to stall the gesture.
    const wrapperRect = this.#measure();
    if (!wrapperRect.width || !wrapperRect.height) return;

    // Keep scale between MIN_ZOOM (1) and MAX_ZOOM (5)
    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    // At minimum zoom (1x), the full image should be visible with no dragging allowed
    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      return;
    }

    // Calculate ACTUAL image content dimensions at current zoom
    // The image element may fill the wrapper, but the content has its own aspect ratio
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    // Try to get natural dimensions
    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      // Fallback: assume square image if we can't get natural dimensions
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    // Calculate how the image fits within the wrapper (object-fit: contain behavior)
    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      // Image is wider - width fits exactly, height is smaller (letterboxed top/bottom)
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      // Image is taller - height fits exactly, width is smaller (letterboxed left/right)
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    // Apply current zoom scale
    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    // SIMPLE APPROACH: Calculate constraints directly from image content dimensions
    // If image content is larger than wrapper, calculate max translation directly

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    // Max translation is half the overflow (since image starts centered)
    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    // Apply symmetric constraints (object-fit: contain behavior)
    // Image starts centered, can move maxTranslate in each direction
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    // Apply final transforms to CSS
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  /**
   * Request an animation frame to update the transform
   */
  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  /**
   * Cancel any pending animation frame
   */
  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  /**
   * Reset zoom to default state (1.5x scale, centered position)
   * Called when zoom is exited/closed
   */
  #resetZoom = () => {
    // Reset scale and translation to defaults
    this.#metrics = null;
    this.#hasManualZoom = false;
    this.#scale = this.#initialScale();
    this.#startScale = this.#scale;
    this.#translate.x = 0;
    this.#translate.y = 0;

    // Reset gesture state to prevent interference on next zoom open
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;

    // Update CSS properties to reflect reset state
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');
  };

  /**
   * Reset to the CURRENT breakpoint's initial scale/position. `connectedCallback`
   * captures the scale at page-load width and nothing recomputes it on resize, so
   * after a desktop→mobile resize the modal would open at the stale desktop scale
   * (1× → image tiny/contained with big margins instead of the mobile 1.5× fill).
   * The zoom dialog calls this on every open so the scale always matches the live
   * breakpoint.
   */
  resetToInitial() {
    this.#resetZoom();
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * Calculate distance between two points or touches
 * @param {Point | Touch} point1 - First point or touch
 * @param {Point | Touch} point2 - Second point or touch
 * @returns {number} Distance between the points
 */
function getDistance(point1, point2) {
  // Handle both Point objects (x, y) and Touch objects (clientX, clientY)
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
